import { getCookie } from '../storage/cookieStorage';
import { getSessionStorageJson, storeSessionStorage } from '../storage/sessionStorage';

const STORAGE_KEY = 'tracking_attribution_snapshot';
const COOKIE_NAMES = ['_fbp', '_fbc'] as const;
const CLICK_ID_NAMES = ['fbclid', 'gclid'] as const;
const UTM_PARAM_NAMES = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

type SnapshotSection = Record<string, string | null>;

export interface AttributionSnapshot {
    landingUrl: string | null;
    referrerUrl: string | null;
    collectedAt: string;
    cookies: SnapshotSection;
    clickIds: SnapshotSection;
    utmParams: SnapshotSection;
    sessionContext: {
        userAgent: string | null;
        locale: string | null;
    };
}

function emptySection(keys: readonly string[]): SnapshotSection {
    return Object.fromEntries(keys.map((key) => [key, null]));
}

function trimToNull(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    return trimmed !== '' ? trimmed : null;
}

function readStoredSnapshot(): Partial<AttributionSnapshot> {
    return getSessionStorageJson(STORAGE_KEY, {}) as Partial<AttributionSnapshot>;
}

function mergeSection(keys: readonly string[], stored: unknown, current: SnapshotSection): SnapshotSection {
    const storedRecord = typeof stored === 'object' && stored !== null ? (stored as SnapshotSection) : {};

    return Object.fromEntries(
        keys.map((key) => [key, current[key] ?? trimToNull(storedRecord[key])])
    ) as SnapshotSection;
}

function mergeFirstTouchSection(keys: readonly string[], stored: unknown, current: SnapshotSection): SnapshotSection {
    const storedRecord = typeof stored === 'object' && stored !== null ? (stored as SnapshotSection) : {};

    return Object.fromEntries(
        keys.map((key) => [key, trimToNull(storedRecord[key]) ?? current[key]])
    ) as SnapshotSection;
}

export function collectAttributionSnapshot(): AttributionSnapshot {
    const stored = readStoredSnapshot();
    const searchParams = new URLSearchParams(window.location.search);
    const currentCookies = Object.fromEntries(COOKIE_NAMES.map((key) => [key, trimToNull(getCookie(key))]));
    const currentClickIds = Object.fromEntries(CLICK_ID_NAMES.map((key) => [key, trimToNull(searchParams.get(key))]));
    const currentUtmParams = Object.fromEntries(UTM_PARAM_NAMES.map((key) => [key, trimToNull(searchParams.get(key))]));

    const snapshot: AttributionSnapshot = {
        landingUrl: trimToNull(stored.landingUrl) ?? trimToNull(window.location.href),
        referrerUrl: trimToNull(stored.referrerUrl) ?? trimToNull(document.referrer),
        collectedAt: trimToNull(stored.collectedAt) ?? new Date().toISOString(),
        cookies: mergeSection(COOKIE_NAMES, stored.cookies, {
            ...emptySection(COOKIE_NAMES),
            ...currentCookies,
        }),
        clickIds: mergeFirstTouchSection(CLICK_ID_NAMES, stored.clickIds, {
            ...emptySection(CLICK_ID_NAMES),
            ...currentClickIds,
        }),
        utmParams: mergeFirstTouchSection(UTM_PARAM_NAMES, stored.utmParams, {
            ...emptySection(UTM_PARAM_NAMES),
            ...currentUtmParams,
        }),
        sessionContext: {
            userAgent: trimToNull(window.navigator.userAgent) ?? trimToNull(stored.sessionContext?.userAgent),
            locale: trimToNull(window.navigator.language) ?? trimToNull(stored.sessionContext?.locale),
        },
    };

    storeSessionStorage(STORAGE_KEY, JSON.stringify(snapshot));

    return snapshot;
}

export function attachAttributionSnapshotInput(form: HTMLFormElement, fieldName = 'tracking_attribution'): void {
    const existingInput = form.querySelector(`input[name="${fieldName}"]`) as HTMLInputElement | null;
    const input = existingInput ?? document.createElement('input');

    input.type = 'hidden';
    input.name = fieldName;
    input.value = JSON.stringify(collectAttributionSnapshot());

    if (!existingInput) {
        form.appendChild(input);
    }
}
